import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Button, List, ListItem, ListItemText, Modal, TextField, IconButton, MenuItem, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useLocation, useNavigate } from 'react-router-dom';
import { addDoctorVisit, fetchDoctorVisits, updateDoctorVisit, deleteDoctorVisit } from '../../services/doctorVisitService';
import { fetchProviders } from '../../services/providerService';
import { buildProviderReviewNavigationState } from '../../utils/providerReviewNavigation';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

const formatProviderLabel = (provider = {}) => (
  [provider.name, provider.specialty].filter(Boolean).join(' · ')
);

const resolveDoctorVisitDisplay = (visit = {}, providers = []) => {
  const provider = Array.isArray(providers)
    ? providers.find((item) => item.id === visit.providerId) || null
    : null;

  if (provider) {
    return {
      label: formatProviderLabel(provider),
      source: 'provider',
    };
  }

  return {
    label: [visit.doctorName, visit.specialty].filter(Boolean).join(' · '),
    source: 'snapshot',
  };
};

const DoctorVisitsTab = ({ childId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = `${location.pathname || ''}${location.search || ''}` || '/dashboard';
  const [visits, setVisits] = useState([]);
  const [providers, setProviders] = useState([]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [currentVisit, setCurrentVisit] = useState(null);
  const [visitForm, setVisitForm] = useState({
    visitDate: '',
    doctorName: '',
    specialty: '',
    providerId: '',
    reasonForVisit: '',
    diagnosis: '',
    treatmentRecommendations: '',
    summaryNotes: '',
    followUpDate: '',
  });

  useEffect(() => {
    let isActive = true;

    if (!childId) {
      setVisits([]);
      return undefined;
    }

    const loadVisits = async () => {
      try {
        const fetchedVisits = await fetchDoctorVisits(childId);
        if (isActive) {
          setVisits(fetchedVisits);
        }
      } catch (error) {
        if (isActive) {
          setVisits([]);
        }
      }
    };

    loadVisits();

    return () => {
      isActive = false;
    };
  }, [childId]);

  useEffect(() => {
    let isActive = true;

    if (!childId) {
      setProviders([]);
      return undefined;
    }

    const loadProviders = async () => {
      try {
        const fetchedProviders = await fetchProviders(childId);
        if (isActive) {
          setProviders(fetchedProviders);
        }
      } catch (error) {
        if (isActive) {
          setProviders([]);
        }
      }
    };

    loadProviders();

    return () => {
      isActive = false;
    };
  }, [childId]);

  const activeProviders = useMemo(() => (
    providers.filter((provider) => provider?.isActive !== false)
  ), [providers]);
  const currentProvider = useMemo(() => (
    providers.find((provider) => provider.id === visitForm.providerId) || null
  ), [providers, visitForm.providerId]);
  const providerOptions = useMemo(() => {
    if (currentProvider && currentProvider.isActive === false && !activeProviders.some((provider) => provider.id === currentProvider.id)) {
      return [currentProvider, ...activeProviders];
    }

    return activeProviders;
  }, [activeProviders, currentProvider]);

  const handleOpenAddModal = (visit = null) => {
    setCurrentVisit(visit);
    if (visit) {
      setVisitForm({
        visitDate: visit.visitDate,
        doctorName: visit.doctorName,
        specialty: visit.specialty,
        providerId: visit.providerId || '',
        reasonForVisit: visit.reasonForVisit,
        diagnosis: visit.diagnosis || '',
        treatmentRecommendations: visit.treatmentRecommendations || '',
        summaryNotes: visit.summaryNotes || '',
        followUpDate: visit.followUpDate || '',
      });
    } else {
      setVisitForm({
        visitDate: '',
        doctorName: '',
        specialty: '',
        providerId: '',
        reasonForVisit: '',
        diagnosis: '',
        treatmentRecommendations: '',
        summaryNotes: '',
        followUpDate: '',
      });
    }
    setOpenAddModal(true);
  };

  const handleCloseAddModal = () => {
    setOpenAddModal(false);
    setCurrentVisit(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setVisitForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    const payload = {
      ...visitForm,
      providerId: visitForm.providerId || null,
    };

    if (currentVisit) {
      await updateDoctorVisit(currentVisit.id, payload);
    } else {
      await addDoctorVisit({ ...payload, childId });
    }
    const fetchedVisits = await fetchDoctorVisits(childId);
    setVisits(fetchedVisits);
    handleCloseAddModal();
  };

  const handleDelete = async (visitId) => {
    if (window.confirm('Are you sure you want to delete this doctor visit?')) {
      await deleteDoctorVisit(visitId);
      const fetchedVisits = await fetchDoctorVisits(childId);
      setVisits(fetchedVisits);
    }
  };

  const handleReviewProvider = (visit) => {
    navigate('/providers', {
      state: buildProviderReviewNavigationState({
        childId,
        returnTo: currentPath,
        originEntryId: visit.id,
        originDate: visit.visitDate,
        originSourceId: visit.providerId || visit.id,
        visitId: visit.id,
        providerName: visit.doctorName || '',
        providerSpecialty: visit.specialty || '',
        organizationName: visit.organizationName || '',
        doctorVisit: visit,
      }),
    });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Doctor Visits</Typography>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => handleOpenAddModal()}
        sx={{ mb: 2 }}
      >
        Add New Doctor Visit
      </Button>

      <List>
        {visits.map((visit) => {
          const display = resolveDoctorVisitDisplay(visit, providers);
          const title = display.label || visit.doctorName || 'Doctor visit';

          return (
            <ListItem
              key={visit.id}
              secondaryAction={
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {!visit.providerId && (visit.doctorName || visit.specialty) ? (
                    <Button size="small" onClick={() => handleReviewProvider(visit)}>
                      Review provider
                    </Button>
                  ) : null}
                  <IconButton edge="end" aria-label="edit" onClick={() => handleOpenAddModal(visit)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(visit.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              }
            >
              <ListItemText
                primary={`${title} on ${visit.visitDate}`}
                primaryTypographyProps={{ variant: 'subtitle1', component: 'div', sx: { fontWeight: 'bold', mb: 1, color: theme.palette.primary.main } }}
                secondary={
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.primary"><strong>Reason:</strong> {visit.reasonForVisit}</Typography>
                    {visit.diagnosis && <Typography variant="body2" color="text.primary"><strong>Diagnosis:</strong> {visit.diagnosis}</Typography>}
                    {visit.treatmentRecommendations && <Typography variant="body2" color="text.primary"><strong>Treatment:</strong> {visit.treatmentRecommendations}</Typography>}
                    {visit.followUpDate && <Typography variant="body2" color="text.primary"><strong>Follow-up:</strong> {visit.followUpDate}</Typography>}
                    {visit.summaryNotes && <Typography variant="body2" color="text.primary"><strong>Notes:</strong> {visit.summaryNotes}</Typography>}
                  </Box>
                }
                secondaryTypographyProps={{ component: 'div' }}
              />
            </ListItem>
          );
        })}
      </List>

      <Modal
        open={openAddModal}
        onClose={handleCloseAddModal}
        aria-labelledby="add-edit-visit-title"
        aria-describedby="add-edit-visit-description"
      >
        <Box sx={style}>
          <Typography id="add-edit-visit-title" variant="h6" component="h2">
            {currentVisit ? 'Edit Doctor Visit' : 'Add New Doctor Visit'}
          </Typography>
          <TextField
            margin="normal"
            required
            fullWidth
            id="visitDate"
            label="Visit Date"
            name="visitDate"
            type="date"
            InputLabelProps={{
              shrink: true,
            }}
            value={visitForm.visitDate}
            onChange={handleChange}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="doctorName"
            label="Doctor's Name"
            name="doctorName"
            value={visitForm.doctorName}
            onChange={handleChange}
          />
          <TextField
            margin="normal"
            fullWidth
            id="specialty"
            label="Specialty"
            name="specialty"
            value={visitForm.specialty}
            onChange={handleChange}
          />
          <TextField
            select
            margin="normal"
            fullWidth
            id="providerId"
            label="Provider"
            name="providerId"
            value={visitForm.providerId}
            onChange={handleChange}
          >
            <MenuItem value="">No provider</MenuItem>
            {providerOptions.map((provider) => {
              const label = formatProviderLabel(provider) || provider.id;
              const isCurrentInactiveProvider = provider.id === currentProvider?.id && provider.isActive === false;
              return (
                <MenuItem key={provider.id} value={provider.id} disabled={provider.isActive === false && !isCurrentInactiveProvider}>
                  {label}{isCurrentInactiveProvider ? ' (inactive)' : ''}
                </MenuItem>
              );
            })}
          </TextField>
          <TextField
            margin="normal"
            fullWidth
            id="reasonForVisit"
            label="Reason for Visit"
            name="reasonForVisit"
            multiline
            rows={2}
            value={visitForm.reasonForVisit}
            onChange={handleChange}
          />
          <TextField
            margin="normal"
            fullWidth
            id="diagnosis"
            label="Diagnosis"
            name="diagnosis"
            multiline
            rows={2}
            value={visitForm.diagnosis}
            onChange={handleChange}
          />
          <TextField
            margin="normal"
            fullWidth
            id="treatmentRecommendations"
            label="Treatment/Recommendations"
            name="treatmentRecommendations"
            multiline
            rows={3}
            value={visitForm.treatmentRecommendations}
            onChange={handleChange}
          />
          <TextField
            margin="normal"
            fullWidth
            id="followUpDate"
            label="Follow-up Date"
            name="followUpDate"
            type="date"
            InputLabelProps={{
              shrink: true,
            }}
            value={visitForm.followUpDate}
            onChange={handleChange}
          />
          <TextField
            margin="normal"
            fullWidth
            id="summaryNotes"
            label="Summary Notes"
            name="summaryNotes"
            multiline
            rows={4}
            value={visitForm.summaryNotes}
            onChange={handleChange}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            onClick={handleSubmit}
          >
            {currentVisit ? 'Update Visit' : 'Add Visit'}
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default DoctorVisitsTab;
